import LabelIcon from '@mui/icons-material/Label';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import useSubscriberPosts, { OnlyFansPosts } from '../../hooks/useSubscriberPosts';
/**
 * Props for the searchable multi-select component
 */
export interface PostLabelMultiSelectProps {
    options: OnlyFansPosts.CreatorPostLabels[];
    value: OnlyFansPosts.CreatorPostLabels[];
    onChange: (next: OnlyFansPosts.CreatorPostLabels[]) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    loading?: boolean;
    /** When provided, the parent can react to search text changes (useful for server filtering). */
    onSearchChange?: (text: string) => void;
    /** Limit how many selected chips are shown before "+N" */
    limitTags?: number;
    /** Title shown in the Card header */
    title?: string;
}

// ---- Virtualized listbox for Autocomplete (react-window) ----
const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data as Array<React.ReactElement>;
    const item = dataSet[index];
    return React.cloneElement(item, {
        style: {
            ...style,
            top: (style as React.CSSProperties).top,
        },
    });
}

const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef<HTMLDivElement>(function OuterElementType(props, ref) {
    const other = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...other} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
    const { children, style, ...other } = props;
    const itemData = React.Children.toArray(children) as React.ReactElement[];
    const itemCount = itemData.length;
    const ITEM_SIZE = 56; // item height in px
    const height = Math.min(8, itemCount) * ITEM_SIZE + 2 * LISTBOX_PADDING;

    // Hide the outer listbox scrollbar; let react-window handle scrolling
    const outerStyle: React.CSSProperties = { ...(style as React.CSSProperties), overflow: 'hidden', padding: 0 };
    const virtualOuterStyle: React.CSSProperties = { padding: LISTBOX_PADDING };

    return (
        <div ref={ref} {...other} style={outerStyle}>
            <OuterElementContext.Provider value={{ ...other, style: virtualOuterStyle }}>
                <VariableSizeList
                    itemData={itemData}
                    height={height}
                    width="100%"
                    outerElementType={OuterElementType as any}
                    innerElementType="ul"
                    itemSize={() => ITEM_SIZE}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

/**
 * A searchable multi-select based on MUI Autocomplete with checkboxes.
 * Designed to handle ~400 options comfortably with client-side filtering.
 */
export default function PostLabelMultiSelect({
    options,
    value,
    onChange,
    label = 'Post Labels',
    placeholder = 'Search labels…',
    size = 'small',
    disabled,
    loading,
    onSearchChange,
    limitTags = 3,
    title = 'Onlyfans Lists',
}: PostLabelMultiSelectProps) {
    // Local input state (so we can display the current query)
    const [inputValue, setInputValue] = React.useState('');
    const { refetch } = useSubscriberPosts();

    // Only custom (user-created) labels are selectable; built-in OnlyFans labels are excluded
    const customOptions = React.useMemo(() => (options ?? []).filter(o => o.type === 'custom'), [options]);

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Stack justifyContent="space-between" direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {loading && <CircularProgress size={16} />}
                        <LabelIcon />
                        <Typography fontWeight={600}>{title}</Typography>
                        <Chip size="small" label={value?.length ?? 0} sx={{ ml: 1 }} />
                    </Stack>
                    <Tooltip title="Reload Lists from OnlyFans">
                        <span>
                            <IconButton
                                disabled={loading}
                                color="secondary"
                                onClick={() => {
                                    refetch();
                                }}
                            >
                                <RefreshIcon fontSize="medium" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                {loading ? (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <LinearProgress sx={{ flexGrow: 1 }} />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                            {customOptions.length ? customOptions.length.toLocaleString() : 0}...
                        </Typography>
                    </Stack>
                ) : null}
                {!loading && (
                    <Autocomplete
                        multiple
                        ListboxComponent={ListboxComponent as any}
                        disableCloseOnSelect
                        filterSelectedOptions
                        options={customOptions}
                        value={value}
                        size={size}
                        disabled={disabled}
                        loading={loading}
                        inputValue={inputValue}
                        onInputChange={(_, v) => {
                            setInputValue(v);
                            onSearchChange?.(v);
                        }}
                        getOptionLabel={o => o.name}
                        isOptionEqualToValue={(opt, val) => opt.labelId === val.labelId}
                        renderInput={params => <TextField {...params} label={label} placeholder={placeholder} />}
                        renderTags={(selected, getTagProps) =>
                            selected.map((opt, index) => (
                                <Chip {...getTagProps({ index })} key={opt.labelId} label={opt.name} size={size} />
                            ))
                        }
                        limitTags={limitTags}
                        noOptionsText={inputValue ? 'No matching lists' : 'No lists'}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.labelId}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" noWrap fontWeight={600} title={option.name}>
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.postCount} posts
                                        </Typography>
                                    </Box>
                                </Stack>
                            </li>
                        )}
                        onChange={(_, next) => onChange(next)}
                    />
                )}
            </CardContent>
        </Card>
    );
}
